import { useMutation } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@autop/ui/src/button';

import { useTRPC } from '@/shared/api';
import { Notice } from '@/shared/ui/notice';

/**
 * "가입한 적 없습니다" 신고 착지 화면 (`/reject?token=...`).
 *
 * 이 메일을 받은 사람이 가입한 적이 없다면 남이 그 주소로 가입했다는 뜻이다.
 * "무시하세요"만 적어 두면 그 사실이 어디에도 남지 않으므로 누를 자리를 준다.
 * 토큰은 인증 링크와 **같은 것**이라 어느 쪽을 누르든 그 링크는 그것으로 끝난다.
 *
 * **인증 화면과 달리 자동으로 보내지 않는다.** 신고는 되돌릴 수 없고 토큰이 즉시 죽는다 —
 * 인증 링크를 누르려다 잘못 눌렀거나, 메일 스캐너가 스크립트를 실행하는 경우에도
 * 신고가 접수돼 버린다. 확인을 한 번 받는 값이 클릭 한 번보다 크다.
 */
export function RejectVerificationPage(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token');
  const trpc = useTRPC();
  const reject = useMutation(trpc.auth.rejectVerification.mutationOptions());

  if (token === null) {
    return (
      <Notice title="링크가 올바르지 않습니다">
        <p className="text-[14px] leading-[1.8] text-muted-foreground">
          메일에 있는 주소를 그대로 눌러 주세요. 주소가 잘려서 열렸을 수 있습니다.
        </p>
      </Notice>
    );
  }

  if (reject.isSuccess) {
    return (
      <Notice title="신고를 접수했습니다">
        <p className="text-[14px] leading-[1.8] text-muted-foreground">
          인증 링크를 무효로 만들었습니다. 그 주소로는 가입이 완료되지 않습니다.
        </p>
        <Link to="/library" className="text-[13px] text-muted-foreground underline">
          자료실 둘러보기
        </Link>
      </Notice>
    );
  }

  return (
    <Notice title="가입하신 적이 없나요?">
      <p className="text-[14px] leading-[1.8] text-muted-foreground">
        아래를 누르면 인증 링크가 <strong>즉시 무효</strong>가 되고, 그 주소로는 가입이 완료되지
        않습니다. 되돌릴 수 없으니 인증하려던 것이라면 메일의 인증 링크를 눌러 주세요.
      </p>
      <Button
        size="lg"
        variant="danger"
        className="w-full"
        disabled={reject.isPending}
        onClick={() => reject.mutate({ token })}
      >
        가입한 적 없습니다
      </Button>
      {reject.error === null ? null : (
        <p className="text-[12.5px] text-destructive">{reject.error.message}</p>
      )}
    </Notice>
  );
}
